'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import SearchInput from '@/app/[locale]/blog/_components/SearchInput'
import AppLink from '@/components/elementary/AppLink'
import { GridEntries } from '@/components/shared/components/GridEntries'
import { MobileCategoryNavigation } from '@/components/shared/components/MobileCategoryNavigation'
import { SidePanelCategoryNavigation } from '@/components/shared/components/SidePanelCategoryNavigation'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import type {
	GridCardProps,
	GridCategoryNavigationData,
	GridCategoryNavigationItem,
	GridTypeProps,
} from '@/types/general'
import type { ServerRenderedEntry } from '@/types/helpers'

function findCategoryBySlug(
	allCategories: GridCategoryNavigationItem[],
	slug?: string | null,
): GridCategoryNavigationItem | undefined {
	if (!slug) return undefined
	return allCategories.find((category) => category?.slug === slug)
}

function filterEntriesByCategory(
	entries: ServerRenderedEntry<GridCardProps>[] | null | undefined,
	categoryDocumentId: string | undefined,
): ServerRenderedEntry<GridCardProps>[] {
	if (!entries) return []
	if (!categoryDocumentId) return entries
	return entries.filter((entry) =>
		entry?.categories?.some((category) => category?.documentId === categoryDocumentId),
	)
}

function filterEntriesBySearchString(
	entries: ServerRenderedEntry<GridCardProps>[],
	searchString: string,
): ServerRenderedEntry<GridCardProps>[] {
	if (!searchString) return entries

	const lowerCaseSearchString = searchString.toLowerCase()

	return entries.filter((entry) => entry.title?.toLowerCase().includes(lowerCaseSearchString))
}

function hasCategories(categories?: GridCategoryNavigationData): boolean {
	return Array.isArray(categories?.allCategories) && categories.allCategories.length > 0
}

function isSearchableType(type: GridTypeProps): boolean {
	return type === 'partners' || type === 'apps'
}

export const GridWithCategoryFilters = ({
	entries,
	categories,
	type,
	additionalLink,
	categorySlug,
}: {
	readonly categories: GridCategoryNavigationData
	readonly entries?: ServerRenderedEntry<GridCardProps>[] | null
	readonly type: GridTypeProps
	readonly additionalLink?: Data.Component<'utilities.link'> | null
	readonly categorySlug?: string | null
}) => {
	const t = useTranslations('GridWithCategoryFilters')
	const { allCategories } = categories

	const currentCategory = findCategoryBySlug(allCategories, categorySlug)

	// If no categorySlug is provided, we're on the "All" page - show all entries
	const allEntries = !categorySlug
		? (entries ?? [])
		: filterEntriesByCategory(entries, currentCategory?.documentId)

	const [filteredEntries, setFilteredEntries] = useState(allEntries)

	const searchItems = (searchString: string) => {
		setFilteredEntries(filterEntriesBySearchString(allEntries, searchString))
	}

	const featuredEntries = filteredEntries.filter((entry) => entry?.isFeatured)
	const restEntries = filteredEntries.filter((entry) => !entry?.isFeatured)

	return (
		<div className="flex lg:flex-row flex-col gap-8 justify-between">
			{hasCategories(categories) && (
				<MobileCategoryNavigation
					{...categories}
					triggerLabel={t(`allLabel.${type}`)}
					categorySlug={categorySlug}
				/>
			)}

			<SidePanelCategoryNavigation
				{...categories}
				additionalLink={additionalLink}
				categorySlug={categorySlug}
			/>

			<div className="flex flex-1 flex-col">
				{isSearchableType(type) && (
					<SearchInput className="mb-6" onInputChange={searchItems} customOnSearch={searchItems} />
				)}

				{filteredEntries.length === 0 && <Paragraph className="mb-6">{t('noResults')}</Paragraph>}

				<div className="flex flex-col gap-7.5">
					{type === 'partners' && (
						<Heading tag="h2" fontSize="displaySmall">
							{currentCategory?.title}
						</Heading>
					)}

					{type === 'apps' && featuredEntries.length > 0 ? (
						<Heading tag="h2" fontSize="displaySmall">
							{t('featuredApps')}
						</Heading>
					) : null}

					{featuredEntries.length > 0 && <GridEntries entries={featuredEntries} type={type} />}

					{type === 'apps' && restEntries.length > 0 ? (
						<Heading tag="h2" fontSize="displaySmall">
							{t('allApps')}
						</Heading>
					) : null}

					{type === 'library' && (
						<Heading tag="h2" fontSize="displaySmall" className="sr-only">
							{currentCategory?.title}
						</Heading>
					)}
					{restEntries.length > 0 && <GridEntries entries={restEntries} type={type} />}
				</div>
			</div>

			{additionalLink?.href && (
				<div className="border-t border-neutral-light-40 pt-9 pb-9 lg:hidden">
					<AppLink
						href={additionalLink.href}
						openExternalInNewTab={additionalLink.newTab ?? false}
						size="none"
						variant="ghost"
					>
						{additionalLink.label}
					</AppLink>
				</div>
			)}
		</div>
	)
}
